import React from 'react';
import { motion } from 'framer-motion';
import { Star, GitFork, ExternalLink, Book, Github } from 'lucide-react';
import { repositories, personalInfo } from '../data/mock';
import { useTheme } from '../context/ThemeContext';
import MouseFollower from '../components/MouseFollower';

const RepositoriesPage = () => {
  const { themeColor } = useTheme();

  const getLanguageColor = (language) => {
    const colors = {
      TypeScript: '#3178c6',
      JavaScript: '#f1e05a',
      Python: '#3572A5',
      PHP: '#4F5D95',
    };
    return colors[language] || '#8b949e';
  };

  const totalStars = repositories.reduce((acc, repo) => acc + repo.stars, 0);
  const totalForks = repositories.reduce((acc, repo) => acc + repo.forks, 0);

  return (
    <div className="min-h-screen pt-24 pb-16">
      <MouseFollower />
      
      {/* Background */}
      <div
        className="fixed bottom-0 left-0 w-1/2 h-1/2 opacity-5 pointer-events-none"
        style={{ background: `radial-gradient(circle at bottom left, ${themeColor.primary}, transparent 70%)` }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 text-sm font-medium"
            style={{ backgroundColor: `rgba(${themeColor.rgb}, 0.1)`, color: themeColor.primary }}
          >
            <Github size={16} />
            GitHub
          </motion.div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Açık Kaynak Projelerim
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto mb-8">
            GitHub'da paylaştığım ve topluluğa katkı sağladığım projeler
          </p>

          {/* Stats */}
          <div className="flex justify-center gap-8">
            <div className="flex items-center gap-2">
              <Star size={20} style={{ color: themeColor.primary }} />
              <span className="text-2xl font-bold text-white">{totalStars}</span>
              <span className="text-zinc-500">Yıldız</span>
            </div>
            <div className="flex items-center gap-2">
              <GitFork size={20} style={{ color: themeColor.primary }} />
              <span className="text-2xl font-bold text-white">{totalForks}</span>
              <span className="text-zinc-500">Fork</span>
            </div>
            <div className="flex items-center gap-2">
              <Book size={20} style={{ color: themeColor.primary }} />
              <span className="text-2xl font-bold text-white">{repositories.length}</span>
              <span className="text-zinc-500">Repo</span>
            </div>
          </div>
        </motion.div>

        {/* Repositories Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {repositories.map((repo, index) => (
            <motion.a
              key={repo.id}
              href={repo.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800/50 hover:border-zinc-700 transition-all"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ y: -5, scale: 1.02 }}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <motion.div
                    className="p-2.5 rounded-xl"
                    style={{ backgroundColor: `rgba(${themeColor.rgb}, 0.1)` }}
                    whileHover={{ rotate: 10 }}
                  >
                    <Book size={20} style={{ color: themeColor.primary }} />
                  </motion.div>
                  <div>
                    <h3 className="font-semibold text-white group-hover:underline decoration-2 underline-offset-4"
                        style={{ textDecorationColor: themeColor.primary }}>
                      {repo.name}
                    </h3>
                    <span className="text-xs text-zinc-500">Public</span>
                  </div>
                </div>
                <ExternalLink size={16} className="text-zinc-600 group-hover:text-zinc-400 transition-colors" />
              </div>

              {/* Description */}
              <p className="text-zinc-500 text-sm mb-4 line-clamp-2 min-h-[40px]">
                {repo.description}
              </p>

              {/* Topics */}
              {repo.topics && repo.topics.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {repo.topics.map((topic) => (
                    <span
                      key={topic}
                      className="px-2 py-0.5 rounded-full text-xs"
                      style={{ backgroundColor: `rgba(${themeColor.rgb}, 0.1)`, color: themeColor.primary }}
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              )}

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-zinc-800/50">
                <div className="flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: getLanguageColor(repo.language) }}
                  />
                  <span className="text-sm text-zinc-400">{repo.language}</span>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1 text-zinc-500 hover:text-yellow-500 transition-colors">
                    <Star size={14} />
                    <span className="text-sm">{repo.stars}</span>
                  </div>
                  <div className="flex items-center gap-1 text-zinc-500 hover:text-zinc-300 transition-colors">
                    <GitFork size={14} />
                    <span className="text-sm">{repo.forks}</span>
                  </div>
                </div>
              </div>
            </motion.a>
          ))}
        </div>

        {/* View All Button */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <motion.a
            href={personalInfo.github}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-medium text-black"
            style={{ backgroundColor: themeColor.primary }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Github size={20} />
            Tüm Repoları Gör
            <ExternalLink size={16} />
          </motion.a>
        </motion.div>
      </div>
    </div>
  );
};

export default RepositoriesPage;
